import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import classNames from 'classnames'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAllUserSharesQuery } from '../../../store/user/user'
import { SegmentedControl } from '../../SegmentedControl/SegmentedControl'
import { hasEnoughMeetingsForScore } from '../scoreRoster'
import { useUserScoresData } from '../useUserScoresData'
import { useUserScoreResultData } from './useUserScoreResultData'
import { buildAreasToImprove, selectTopMeetings } from '../userScoreResultAreas'
import { RANGE_OPTIONS, RANGE_PERIOD_TEXT, type RangeKey } from '../userScoreRange'
import { UserScoreCard } from '../UserScoreCard/UserScoreCard'
import { UserScoreHistoryCard } from '../UserScoreHistoryCard/UserScoreHistoryCard'
import { UserAreasToImproveCard } from '../UserAreasToImproveCard/UserAreasToImproveCard'
import { TopMeetingsCard } from '../TopMeetingsCard/TopMeetingsCard'
import { WhatsCounted } from '../WhatsCounted/WhatsCounted'
import { InsightsEmptyState } from '../../../shared/insights/components/InsightsEmptyState/InsightsEmptyState'
import styles from './UserScoreDetails.module.scss'

interface UserScoreDetailsProps {
  userId: string
  subjectPhrase?: string
  subjectNoun?: string
  firstName?: string
  subtitlePending?: boolean
  heading?: ReactNode
  initialRange?: RangeKey
  onRangeChange?: (range: RangeKey) => void
  onLoadingChange?: (loading: boolean) => void
  isTopPerformer?: boolean
}

export const UserScoreDetails: FC<UserScoreDetailsProps> = ({
  userId,
  subjectPhrase,
  subjectNoun = 'This person',
  firstName,
  subtitlePending = false,
  heading,
  initialRange = '90d',
  onRangeChange,
  onLoadingChange,
  isTopPerformer = false,
}) => {
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const { data: meetingShares, isFetching: isFetchingShares } = useGetAllUserSharesQuery({
    accountId: currentUser.accountId,
    userId: currentUser.id,
    shareType: 'meeting',
  })
  // The user always sees their own meetings; otherwise the displayed user must have shared their meetings with them
  const canViewMeetings = userId === currentUser.id || (meetingShares?.data.some((share) => share.ownerUserId === userId) ?? false)
  const [range, setRange] = useState<RangeKey>(initialRange)
  const subject = subjectPhrase ?? (firstName ? `${firstName} is` : 'this user is')

  const { scorecards, isFetching: isFetchingScore } = useUserScoresData(accountId ?? '', range, { userIds: [userId] })
  const row = scorecards.find((scorecard) => scorecard.user_id === userId)
  const meetingCount = row?.meetings_current ?? 0
  const hasScore = hasEnoughMeetingsForScore(meetingCount, row?.avg_score_current)
  // Nothing in the period carries a score, so there are no cards worth rendering
  const hasNoScoredMeetings = row?.avg_score_current == null
  const hasPreviousPeriodData = (row?.meetings_previous ?? 0) > 0

  const { rows, isFetching: isFetchingAreas } = useUserScoreResultData(accountId ?? '', userId, range)
  const areas = useMemo(() => (hasScore ? buildAreasToImprove(rows, meetingCount) : []), [hasScore, rows, meetingCount])
  const topMeetings = useMemo(() => (isTopPerformer && hasScore ? selectTopMeetings(rows) : []), [isTopPerformer, hasScore, rows])

  const loading = isFetchingScore || isFetchingAreas || (isFetchingShares && userId !== currentUser.id)

  useEffect(() => {
    onLoadingChange?.(loading)
  }, [loading, onLoadingChange])

  return (
    <>
      <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap zocks-mb-3'>
        <div>
          {heading}
          {subtitlePending || isFetchingScore || !hasNoScoredMeetings ? (
            <p className='insights-section-subtitle mb-0'>
              {subtitlePending || isFetchingScore ? (
                <span className={classNames('rounded-1', styles.subtitleSkeleton)} role='status' aria-label='Loading meeting score summary' />
              ) : (
                <>
                  See how {subject} scoring across <span className='bold'>{meetingCount}</span> {meetingCount === 1 ? 'meeting' : 'meetings'} in the{' '}
                  <span className='bold'>{RANGE_PERIOD_TEXT[range]}</span> · <WhatsCounted periodText={RANGE_PERIOD_TEXT[range]} />
                </>
              )}
            </p>
          ) : null}
        </div>
        <div className={loading ? 'opacity-50 pe-none' : undefined}>
          <SegmentedControl
            options={RANGE_OPTIONS}
            activeKey={range}
            ariaLabel='Meeting scores time range'
            onSelect={(key) => {
              if (key !== range) {
                setRange(key as RangeKey)
                onRangeChange?.(key as RangeKey)
              }
            }}
          />
        </div>
      </div>
      {loading ? (
        <div className={classNames('insights-initial-loading', styles.loadingPanel)}>
          <div className='insights-loading-spinner' />
          <div className='insights-loading-text'>Collecting meeting scores...</div>
        </div>
      ) : hasNoScoredMeetings ? (
        <div key={range} className={styles.contentEnter}>
          <InsightsEmptyState
            title={`No scored meetings in the ${RANGE_PERIOD_TEXT[range]}`}
            subtitle={
              meetingCount > 0
                ? `${subjectNoun} had ${meetingCount} ${meetingCount === 1 ? 'meeting' : 'meetings'} in this period, but none were scored.`
                : `${subjectNoun} had no meetings in this period.`
            }
          />
        </div>
      ) : (
        <Row key={range} className={classNames('g-4', styles.contentEnter)}>
          <Col xs={12} lg={4}>
            <UserScoreCard
              score={hasScore ? (row?.avg_score_current ?? null) : null}
              delta={hasScore && hasPreviousPeriodData ? (row?.avg_score_trend ?? null) : null}
              range={range}
            />
          </Col>
          <Col xs={12} lg={8}>
            <UserScoreHistoryCard scoreHistory={hasScore ? (row?.score_history ?? []) : []} range={range} />
          </Col>
          {topMeetings.length > 0 ? (
            <Col xs={12}>
              <TopMeetingsCard meetings={topMeetings} canViewMeetings={canViewMeetings} />
            </Col>
          ) : null}
          <Col xs={12}>
            <UserAreasToImproveCard
              areas={areas}
              totalMeetings={meetingCount}
              range={range}
              userId={userId}
              hasScore={hasScore}
              canViewMeetings={canViewMeetings}
            />
          </Col>
        </Row>
      )}
    </>
  )
}
